
import { AnalyticsEvent } from "@/types/analytics";

/**
 * Base class for all analytics services
 * Provides common event tracking functionality with consent management
 */
export class BaseAnalytics {
  protected consentManager?: { hasConsent: (type: string) => boolean };
  
  constructor(consentManager?: { hasConsent: (type: string) => boolean }) {
    this.consentManager = consentManager;
  }
  
  /**
   * Check if tracking is allowed based on consent settings
   */
  protected isTrackingAllowed(category: string): boolean {
    if (!this.consentManager) {
      // Default to allowed if no consent manager is provided
      return true;
    }
    
    switch (category.toLowerCase()) {
      case 'performance':
        // Performance tracking is considered essential
        return true;
      case 'marketing':
        return this.consentManager.hasConsent('marketing');
      case 'analytics':
        return this.consentManager.hasConsent('analytics');
      case 'preferences':
        return this.consentManager.hasConsent('preferences');
      default:
        // Essential tracking is always allowed
        return true;
    }
  }
  
  /**
   * Track an analytics event with consent check
   */
  protected trackEvent(event: AnalyticsEvent): void {
    try {
      if (this.isTrackingAllowed(event.category)) {
        // In a real implementation, this would send to an analytics service
        console.log('Analytics event:', event);
      } else {
        // Log that event was blocked due to consent
        console.debug('Analytics event blocked due to consent settings:', event);
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }
  
  /**
   * Safe method to track performance events
   * This is exposed for performance trackers to use
   */
  public trackPerformanceEvent(event: {
    category: string;
    action: string;
    label?: string;
    value?: number;
    metadata?: Record<string, any>;
  }): void {
    // Ensure category is always 'performance' for these events
    this.trackEvent({
      ...event,
      category: 'performance'
    });
  }
}
